import { LargeHeadingStyled } from "../../../components/custom/texts/texts.styled";
import { Button, Card, Drawer, Flex, Form } from "@nubeteck/components";
import { Input, Layout, Table, DatePicker, InputNumber, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import React from "react";
import toast from "react-hot-toast";
import { FormInstance } from "antd/lib/form";
import { toastErrorStyle, toastSuccessStyle } from "src/constants";
import dayjs, { Dayjs } from "dayjs";
import { useGetAdminPeriodsQuery } from "src/services";

import {
  useGetZeroWeekDiscountsQuery,
  useInsertZeroWeekDiscountMutation,
  useUpdateZeroWeekDiscountMutation,
  useDeleteZeroWeekDiscountMutation,
} from "src/services/zeroWeekDiscountServices";
import { ArrayUtils } from "@nubeteck/utils";

const { Content } = Layout;

interface IZeroWeekDiscount {
  id: number;
  fecha: Dayjs;
  porcentaje: number;
  anio: number;
  periodo: string;
  status: string;
}

const ZeroWeekDiscount: React.FC = () => {
  const [form] = Form.useForm<IZeroWeekDiscount>();
  const [discountId, setDiscountId] = React.useState(0);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [anio, setAnio] = React.useState(new Date().getFullYear());
  const [filtro, setFiltro] = React.useState<string>("");
  const { data: periodos } = useGetAdminPeriodsQuery(anio);

  const formRef = React.useRef<FormInstance>(null);

  const { data, isLoading, refetch } = useGetZeroWeekDiscountsQuery(undefined);
  const [
    insertDiscount,
    {
      isLoading: isLoadingCreate,
      isSuccess: isSuccessCreate,
      isError: isErrorCreate,
      error: errorCreate,
    },
  ] = useInsertZeroWeekDiscountMutation();
  const [
    updateDiscount,
    {
      isLoading: isLoadingUpdate,
      isSuccess: isSuccessUpdate,
      isError: isErrorUpdate,
      error: errorUpdate,
    },
  ] = useUpdateZeroWeekDiscountMutation();
  const [
    deleteDiscount,
    {
      isLoading: isLoadingDelete,
      isSuccess: isSuccessDelete,
      isError: isErrorDelete,

      error: errorDelete,
    },
  ] = useDeleteZeroWeekDiscountMutation();
  const periodosOptions = ArrayUtils.selectLabelValue(periodos?.data ?? [], "nombre", "nombre");

  React.useEffect(() => {
    if (isSuccessCreate || isSuccessUpdate || isSuccessDelete) {
      toast.success("Operación realizada con éxito", toastSuccessStyle);
      form.resetFields();
      setDiscountId(0);
      setDrawerOpen(false);
      refetch();
    }
    if (isErrorCreate) {
      toast.error(
        errorCreate?.data?.message ?? JSON.parse(errorCreate?.data)?.message,
        toastErrorStyle,
      );
    }
    if (isErrorUpdate) {
      toast.error(
        errorUpdate?.data?.message ?? JSON.parse(errorUpdate?.data)?.message,
        toastErrorStyle,
      );
    }
    if (isErrorDelete) {
      toast.error(
        errorDelete?.data?.message ?? JSON.parse(errorDelete?.data)?.message,
        toastErrorStyle,
      );
    }
  }, [
    isSuccessCreate,
    isSuccessUpdate,
    isSuccessDelete,
    isErrorCreate,
    isErrorUpdate,
    isErrorDelete,
    form,
    refetch,
    errorCreate,
    errorUpdate,
    errorDelete,
  ]);

  const onClickEditar = React.useCallback(
    (record: IZeroWeekDiscount) => {
      setDiscountId(record.id);
      form.setFieldsValue({
        ...record,
        fecha: dayjs(record.fecha),
      });
      setDrawerOpen(true);
    },
    [form],
  );

  const submit = (values: any) => {
    // const localTime = values.fecha; // Esto es un objeto Day.js local (e.g., 00:00:00 GMT-4)

    // // Convierte el objeto Day.js a una cadena ISO 8601 en formato UTC (04:00:00Z)
    // const timeToStore = localTime.format("YYYY-MM-DD HH:mm:ss");
    // console.log(timeToStore, "klk");

    const payload = {
      ...values,
      fecha: values.fecha.format("YYYY-MM-DDTHH:mm:ss"),
    };
    console.log(payload, "test");
    if (discountId !== 0) {
      updateDiscount({ id: discountId, ...payload });
    } else {
      insertDiscount(payload);
    }
  };

  const columns = React.useMemo<ColumnsType<IZeroWeekDiscount>>(() => {
    const onDelete = (id: number) => {
      deleteDiscount(id);
    };

    return [
      {
        title: "Fecha",
        dataIndex: "fecha",
        render: (val) => dayjs(val).format("DD/MM/YYYY HH:mm:ss"),
      },
      {
        title: "Porcentaje",
        dataIndex: "porcentaje",
        render: (val) => `${val}%`,
      },
      {
        title: "Año",
        dataIndex: "anio",
      },
      {
        title: "Periodo",
        dataIndex: "periodo",
      },
      {
        title: "Estado",
        dataIndex: "status",
      },
      {
        title: "Acciones",
        dataIndex: "actions",
        render: (_, record) => (
          <Flex style={{ gap: "10px" }}>
            <Button size="small" onClick={() => onClickEditar(record)}>
              Editar
            </Button>
            <Button
              size="small"
              danger
              onClick={() => onDelete(record.id)}
              loading={isLoadingDelete}
            >
              Eliminar
            </Button>
          </Flex>
        ),
      },
    ];
  }, [onClickEditar, isLoadingDelete, deleteDiscount]);

  const filteredData = data?.data?.filter((d: any) =>
    d.periodo.toLowerCase().includes(filtro.toLowerCase()),
  );

  return (
    <Flex style={{ flexDirection: "column", width: "100%", alignSelf: "flex-start" }}>
      <Content style={{ marginTop: "16px" }}>
        <Flex style={{ width: "100%", justifyContent: "space-between" }}>
          <LargeHeadingStyled>Descuentos semana cero</LargeHeadingStyled>
          <Flex style={{ gap: "15px" }}>
            <Form.Item label="Búsqueda por periodo">
              <Input
                type="text"
                size="small"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                placeholder="Periodo"
                style={{ height: "35px", marginRight: "15px" }}
              />
            </Form.Item>
            <Button
              type="primary"
              onClick={() => {
                setDrawerOpen(true);
                form.resetFields();
              }}
            >
              Nuevo descuento
            </Button>
          </Flex>
        </Flex>
        <Card bodyStyle={{ padding: 0, width: "100%" }}>
          <Table<IZeroWeekDiscount>
            loading={isLoading || isLoadingCreate || isLoadingUpdate}
            columns={columns}
            dataSource={filteredData}
            rowKey={(v) => v.id}
            pagination={{
              defaultPageSize: 5,
              showSizeChanger: true,
              style: { marginRight: "30px" },
            }}
            scroll={{ x: 800 }}
          />
          <Drawer
            placement="right"
            title={discountId !== 0 ? "Editar descuento" : "Nuevo descuento"}
            open={drawerOpen}
            onClose={() => {
              setDiscountId(0);
              setDrawerOpen(false);
            }}
          >
            <Form
              form={form}
              layout="vertical"
              ref={formRef}
              onFinish={submit}
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
            >
              <Form.Item
                name="fecha"
                label="Fecha"
                rules={[{ required: true, message: "Este campo es requerido" }]}
              >
                <DatePicker showTime format="DD/MM/YYYY HH:mm:ss" style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                name="porcentaje"
                label="Porcentaje"
                rules={[{ required: true, message: "Este campo es requerido" }]}
              >
                <InputNumber id="input-normal" min={0} max={100} style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                name="anio"
                label="Año"
                rules={[{ required: true, message: "Este campo es requerido" }]}
              >
                <InputNumber
                  min={new Date().getFullYear()}
                  style={{ width: "100%", textAlign: "start" }}
                  id="input-normal"
                  onChange={(val) => setAnio(val ?? new Date().getFullYear())}
                />
              </Form.Item>
              <Form.Item
                name="periodo"
                label="Periodo"
                rules={[{ required: true, message: "Este campo es requerido" }]}
              >
                <Select options={periodosOptions} />
              </Form.Item>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                style={{ alignSelf: "flex-end" }}
                loading={isLoadingCreate || isLoadingUpdate}
              >
                Guardar
              </Button>
            </Form>
          </Drawer>
        </Card>
      </Content>
    </Flex>
  );
};

export default ZeroWeekDiscount;
